import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ProgressUpdate } from "../../types/api";

interface ChatProgressProps {
  /** The progress update object containing step information. */
  progress: ProgressUpdate;
}

/**
 * AnimatedEllipsis Component
 *
 * Displays an animated ellipsis (...) to indicate ongoing progress.
 * The ellipsis animation is achieved by cycling through ".", "..", and "..." at a set interval.
 */
const AnimatedEllipsis: React.FC = () => {
  const [dots, setDots] = useState("");

  useEffect(() => {
    // Set up an interval to update the ellipsis dots every 500ms
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return ""; // Reset if it reaches "..."
        return prev + "."; // Add a dot
      });
    }, 500);

    // Clean up the interval when the component unmounts to prevent memory leaks
    return () => clearInterval(interval);
  }, []);

  return <span>{dots}</span>;
};

/**
 * ChatProgress Component
 *
 * Displays the current progress of a task, including the step name and an animated ellipsis
 * while the task is in progress.
 *
 * @param {ChatProgressProps} props - Component props
 * @returns {JSX.Element} Rendered ChatProgress component
 */
export const ChatProgress: React.FC<ChatProgressProps> = ({ progress }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}
    >
      <Box
        sx={{
          color: theme.palette.text.secondary,
          fontSize: "0.875rem",
          lineHeight: 1.5,
          ml: -1.5, // Shift the box a little bit to the left
        }}
      >
        {/* TODO: What should we do with the step name? e.g. progress.step_name */}
        {/* Maybe we show the step name and the description overwrites it? */}
        {progress.detail.description}
        <AnimatedEllipsis />
      </Box>
    </Box>
  );
};
